import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, type VeiculoComDocumentos } from "../api";
import ErrorState from "./ErrorState";
import { IconSearch, IconFile, IconEye, IconDownload, IconRefresh } from "../icons";

/**
 * DOCUMENTOS DOS VEÍCULOS — consulta da equipe.
 *
 * A frota com a placa, o modelo e os dados do cadastro, e os documentos de
 * cada veículo (CRLV, seguro, IPVA...) para ver e baixar. Quem anexa é a
 * Gestão da Operação (mesmo banco); aqui é só leitura.
 *
 * "Ver" e "Baixar" são links comuns com assinatura (server/arquivo-link.ts),
 * abertos em outra aba: no app instalado no celular, navegar para o PDF na
 * própria janela deixaria a pessoa presa no arquivo, sem voltar ao app.
 */

/** Os links assinados valem 4 h; recarrega antes disso quando a tela volta. */
const RECARREGAR_APOS_MS = 2 * 60 * 60_000;

/** AAAA-MM-DD → DD/MM/AAAA (sem passar por Date, para não escorregar de fuso). */
function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

function hojeIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Vencido / vence em até 30 dias / válido. */
function situacaoValidade(validade: string | null): { texto: string; tom: "danger" | "warn" | "neutral" } | null {
  if (!validade) return null;
  const hoje = hojeIso();
  if (validade < hoje) return { texto: `Vencido em ${dataBR(validade)}`, tom: "danger" };
  const dias = Math.round(
    (Date.parse(`${validade}T12:00:00Z`) - Date.parse(`${hoje}T12:00:00Z`)) / 86_400_000,
  );
  if (dias <= 30) return { texto: `Vence em ${dataBR(validade)}`, tom: "warn" };
  return { texto: `Válido até ${dataBR(validade)}`, tom: "neutral" };
}

/** Cor da situação do VEÍCULO (o statusTone geral lê "Inativo" como "ativo"). */
function tomDoVeiculo(status: string | null): string {
  if (status === "Ativo") return "ok";
  if (status === "Em manutenção") return "warn";
  return "neutral";
}

function tamanhoTexto(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Busca sem acento e sem caixa. */
function chave(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export default function DocumentosVeiculosView() {
  const [veiculos, setVeiculos] = useState<VeiculoComDocumentos[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [busca, setBusca] = useState("");
  const carregadoEm = useRef(0);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setVeiculos(await api.documentosVeiculos.list());
      carregadoEm.current = Date.now();
    } catch (err) {
      setErro(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // App aberto no celular por horas: ao voltar para a tela, renova os links.
  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState === "visible" && Date.now() - carregadoEm.current > RECARREGAR_APOS_MS) {
        void carregar();
      }
    };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => document.removeEventListener("visibilitychange", aoVoltar);
  }, [carregar]);

  const filtrados = useMemo(() => {
    const q = chave(busca.trim());
    if (!q) return veiculos;
    return veiculos.filter((v) =>
      [v.placa, v.modelo, v.marca, v.ano, v.responsavel].some((campo) => chave(campo).includes(q)),
    );
  }, [veiculos, busca]);

  const totalDocs = veiculos.reduce((n, v) => n + v.documentos.length, 0);

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={() => void carregar()} />;

  return (
    <div className="docv">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={16} />
          <input
            className="search"
            type="search"
            value={busca}
            placeholder="Buscar por placa, modelo ou responsável"
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <span className="muted">
            {veiculos.length} {veiculos.length === 1 ? "veículo" : "veículos"} · {totalDocs}{" "}
            {totalDocs === 1 ? "documento" : "documentos"}
          </span>
          <button type="button" className="icon-btn" title="Atualizar" onClick={() => void carregar()}>
            <IconRefresh size={17} />
          </button>
        </div>
      </div>

      {carregando && veiculos.length === 0 ? (
        <div className="empty">Carregando…</div>
      ) : veiculos.length === 0 ? (
        <div className="empty">Nenhum veículo cadastrado.</div>
      ) : filtrados.length === 0 ? (
        <div className="empty">Nenhum veículo encontrado para "{busca}".</div>
      ) : (
        <div className="docv-grid">
          {filtrados.map((v) => (
            <article key={v.id} className="docv-card">
              <header className="docv-head">
                <div className="docv-id">
                  <div className="docv-placa">{v.placa || "Sem placa"}</div>
                  <div className="docv-modelo">
                    {[v.modelo, v.marca, v.ano].filter((x) => x !== null && x !== "").join(" · ") || "—"}
                  </div>
                </div>
                {v.status && <span className={`pill pill-${tomDoVeiculo(v.status)}`}>{v.status}</span>}
              </header>

              <dl className="docv-dados">
                <div>
                  <dt>Responsável</dt>
                  <dd>{v.responsavel || "—"}</dd>
                </div>
                <div>
                  <dt>KM atual</dt>
                  <dd>{v.km_atual != null ? v.km_atual.toLocaleString("pt-BR") : "—"}</dd>
                </div>
              </dl>

              {v.documentos.length === 0 ? (
                <p className="muted docv-vazio">Nenhum documento anexado.</p>
              ) : (
                <ul className="docv-lista">
                  {v.documentos.map((d) => {
                    const sit = situacaoValidade(d.validade);
                    return (
                      <li key={d.id} className="docv-doc">
                        <span className="docv-doc-icone">
                          <IconFile size={18} />
                        </span>
                        <div className="docv-doc-info">
                          <div>
                            <strong>{d.tipo}</strong>
                            {d.descricao ? <span className="muted"> — {d.descricao}</span> : null}
                          </div>
                          <div className="muted docv-doc-meta">
                            {d.nome_arquivo} · {tamanhoTexto(d.tamanho)}
                          </div>
                          {sit &&
                            (sit.tom === "neutral" ? (
                              <div className="muted docv-doc-meta">{sit.texto}</div>
                            ) : (
                              <span className={`pill pill-${sit.tom} docv-validade`}>{sit.texto}</span>
                            ))}
                        </div>
                        <div className="docv-doc-acoes">
                          <a className="btn-secondary docv-btn" href={d.url} target="_blank" rel="noopener">
                            <IconEye size={16} /> Ver
                          </a>
                          <a className="btn-secondary docv-btn" href={d.url_baixar} target="_blank" rel="noopener">
                            <IconDownload size={16} /> Baixar
                          </a>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
